import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Trash from '../../../icons/trash';

interface AnnotationPopoverProps {
    isVisible: boolean;
    position: { top: number; left: number };
    content: string;
    onClose: () => void;
    onSave?: (newContent: string) => void;
    onRemove?: () => void;
    readOnly?: boolean;
}

const AnnotationPopover: React.FC<AnnotationPopoverProps> = ({
    isVisible,
    position,
    content,
    onClose,
    onSave,
    onRemove,
    readOnly = false,
}) => {
    const [editedContent, setEditedContent] = useState(content);
    const popoverRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setEditedContent(content);
    }, [content]);

    // Close on click outside
    useEffect(() => {
        if (!isVisible) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
                onClose();
                // Sauvegarde automatique à la fermeture si le contenu a changé
                if (onSave && editedContent !== content && !readOnly) {
                    onSave(editedContent);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isVisible, onClose, editedContent, content, onSave, readOnly]);

    if (!isVisible) return null;

    return createPortal(
        <div
            ref={popoverRef}
            className="fixed z-[9999] bg-white dark:bg-mcdm-third-500 shadow-md border border-gray-300 dark:border-gray-600 rounded p-2 flex flex-col gap-2 min-w-[300px]"
            style={{
                top: readOnly ? position.top - 5 : position.top + 5, // Décalé vers le haut si readOnly
                left: position.left,
                transform: readOnly ? 'translateY(-100%)' : 'none', // Remonte le bloc de sa propre hauteur si readOnly
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {readOnly ? (
                <div className="text-sm text-gray-800 dark:text-gray-200">
                    <strong>Annotation :</strong> {content}
                </div>
            ) : (
                <>
                    <div className="flex flex-col gap-1">
                        <textarea
                            className="w-full text-sm p-1 border rounded dark:bg-mcdm-third-600 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-mcdm-secondary-500 resize-none"
                            value={editedContent}
                            onChange={(e) => setEditedContent(e.target.value)}
                            rows={2}
                            placeholder="Votre annotation..."
                            autoFocus
                        />
                        <div className="flex justify-between items-center mt-1">
                            <button
                                onClick={() => {
                                    if (onSave) onSave(editedContent);
                                    onClose();
                                }}
                                className="text-xs bg-mcdm-secondary-500 text-white px-2 py-1 rounded hover:bg-mcdm-secondary-600"
                            >
                                OK
                            </button>

                            {onRemove && (
                                <button
                                    onClick={onRemove}
                                    className="flex items-center gap-1 text-red-500 hover:text-red-700 text-xs"
                                    title="Supprimer l'annotation"
                                >
                                    <Trash width="12px" height="12px" fill="currentColor" />
                                    Supprimer
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>,
        document.body
    );
};

export default AnnotationPopover;
